const SECTION_LABELS = Object.freeze({
  navbar: "Encabezado",
  hero: "Portada",
  spotlight: "Destacado",
  featured: "Destacados del catálogo",
  brands: "Marcas",
  categories: "Categorías",
  about: "Sobre el negocio",
  highlights: "Diferenciales",
  contact: "Contacto",
  floatingWhatsapp: "WhatsApp flotante",
});

function text(value) {
  return String(value ?? "").trim();
}

function enabled(value, fallback = true) {
  return value === undefined || value === null ? fallback : value !== false;
}

function plainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function clampChannel(value) {
  return Math.min(255, Math.max(0, Number(value) || 0));
}

function parseHex(value) {
  const raw = value.slice(1);
  if (![3, 4, 6, 8].includes(raw.length)) return null;
  const expanded = raw.length <= 4
    ? raw.split("").map((char) => `${char}${char}`).join("")
    : raw;
  const r = Number.parseInt(expanded.slice(0, 2), 16);
  const g = Number.parseInt(expanded.slice(2, 4), 16);
  const b = Number.parseInt(expanded.slice(4, 6), 16);
  const a = expanded.length === 8 ? Number.parseInt(expanded.slice(6, 8), 16) / 255 : 1;
  return [r, g, b, Number.isFinite(a) ? a : 1];
}

function parseRgb(value) {
  const match = value.match(/^rgba?\(\s*([^)]*)\)$/i);
  if (!match) return null;
  const parts = match[1].split(",").map((part) => part.trim());
  if (parts.length < 3 || parts.length > 4) return null;
  const channels = parts.slice(0, 3).map((part) => {
    if (part.endsWith("%")) return clampChannel((Number.parseFloat(part) / 100) * 255);
    return clampChannel(Number.parseFloat(part));
  });
  if (channels.some((channel) => !Number.isFinite(channel))) return null;
  const alphaRaw = parts[3];
  const alpha = alphaRaw === undefined
    ? 1
    : alphaRaw.endsWith("%")
      ? Number.parseFloat(alphaRaw) / 100
      : Number.parseFloat(alphaRaw);
  return [...channels, Math.min(1, Math.max(0, Number.isFinite(alpha) ? alpha : 1))];
}

function hueToRgb(p, q, t) {
  let resolved = t;
  if (resolved < 0) resolved += 1;
  if (resolved > 1) resolved -= 1;
  if (resolved < 1 / 6) return p + (q - p) * 6 * resolved;
  if (resolved < 1 / 2) return q;
  if (resolved < 2 / 3) return p + (q - p) * (2 / 3 - resolved) * 6;
  return p;
}

function parseHsl(value) {
  const match = value.match(/^hsla?\(\s*([^)]*)\)$/i);
  if (!match) return null;
  const parts = match[1].split(",").map((part) => part.trim());
  if (parts.length < 3 || parts.length > 4) return null;
  const hue = ((Number.parseFloat(parts[0]) % 360) + 360) % 360 / 360;
  const saturation = Number.parseFloat(parts[1]) / 100;
  const lightness = Number.parseFloat(parts[2]) / 100;
  if (![hue, saturation, lightness].every(Number.isFinite)) return null;
  const q = lightness < 0.5
    ? lightness * (1 + saturation)
    : lightness + saturation - lightness * saturation;
  const p = 2 * lightness - q;
  const rgb = saturation === 0
    ? [lightness, lightness, lightness]
    : [hueToRgb(p, q, hue + 1 / 3), hueToRgb(p, q, hue), hueToRgb(p, q, hue - 1 / 3)];
  const alphaRaw = parts[3];
  const alpha = alphaRaw === undefined ? 1 : Number.parseFloat(alphaRaw);
  return [
    ...rgb.map((channel) => clampChannel(channel * 255)),
    Math.min(1, Math.max(0, Number.isFinite(alpha) ? alpha : 1)),
  ];
}

function parseColor(value) {
  const candidate = text(value).toLowerCase();
  if (!candidate || candidate === "transparent" || candidate === "currentcolor") return null;
  if (candidate.startsWith("#")) return parseHex(candidate);
  if (candidate.startsWith("rgb")) return parseRgb(candidate);
  if (candidate.startsWith("hsl")) return parseHsl(candidate);
  return null;
}

function composite(foreground, background) {
  const fg = foreground || [0, 0, 0, 0];
  const bg = background || [255, 255, 255, 1];
  const alpha = fg[3] + bg[3] * (1 - fg[3]);
  if (alpha <= 0) return [255, 255, 255, 0];
  return [
    (fg[0] * fg[3] + bg[0] * bg[3] * (1 - fg[3])) / alpha,
    (fg[1] * fg[3] + bg[1] * bg[3] * (1 - fg[3])) / alpha,
    (fg[2] * fg[3] + bg[2] * bg[3] * (1 - fg[3])) / alpha,
    alpha,
  ];
}

function luminance(rgb) {
  const channels = rgb.slice(0, 3).map((channel) => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

export function landingContrastRatio(foregroundValue, backgroundValue, backgroundUnderlayValue = "") {
  const foreground = parseColor(foregroundValue);
  const background = parseColor(backgroundValue);
  if (!foreground || !background) return null;

  let opaqueBackground = background;
  if (background[3] < 0.999) {
    const underlay = parseColor(backgroundUnderlayValue);
    if (!underlay || underlay[3] < 0.999) return null;
    opaqueBackground = composite(background, underlay);
  }

  const opaqueForeground = foreground[3] < 0.999
    ? composite(foreground, opaqueBackground)
    : foreground;
  const first = luminance(opaqueForeground);
  const second = luminance(opaqueBackground);
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}

function addIssue(issues, issue) {
  const key = `${issue.category}:${issue.path || issue.section || issue.title}`;
  if (issues.some((current) => current.key === key)) return;
  issues.push({
    key,
    severity: "info",
    ...issue,
    sectionLabel: SECTION_LABELS[issue.section] || issue.section || "Sitio",
  });
}

function addContrastIssue(issues, {
  foreground,
  background,
  path,
  section,
  label,
  tool = "colors",
  threshold = 4.5,
}) {
  const ratio = landingContrastRatio(foreground, background);
  if (ratio === null || ratio >= threshold) return;
  addIssue(issues, {
    category: "Contraste",
    severity: ratio < 3 ? "warning" : "info",
    path,
    section,
    tool,
    title: `${label} con contraste bajo`,
    description: `La combinación actual da aproximadamente ${ratio.toFixed(1)}:1. Revisala especialmente para texto pequeño o de lectura frecuente.`,
    detail: `Referencia orientativa: ${threshold}:1 para texto normal.`,
    ratio,
  });
}

function finiteOpacity(value, fallback = 1) {
  if (value === "" || value === null || value === undefined) return fallback;
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(1, Math.max(0, number)) : fallback;
}

function rgbaCss(color) {
  if (!Array.isArray(color) || color.length < 4) return "";
  const alpha = Math.min(1, Math.max(0, Number(color[3]) || 0));
  const channels = color.slice(0, 3).map((channel) => Math.round(clampChannel(channel)));
  return `rgba(${channels[0]}, ${channels[1]}, ${channels[2]}, ${Math.round(alpha * 10000) / 10000})`;
}

function resolveAuditSurface(backgroundValue, opacity = 1, underlayValue = "") {
  const parsed = parseColor(backgroundValue);
  if (!parsed) return "";

  const effective = [...parsed];
  effective[3] = Math.min(1, Math.max(0, effective[3] * finiteOpacity(opacity, 1)));
  if (effective[3] >= 0.999) return rgbaCss(effective);

  const underlay = parseColor(underlayValue);
  if (!underlay || underlay[3] < 0.999) return "";
  return rgbaCss(composite(effective, underlay));
}

function reviewCatalogCardContrast(issues, form, productsEnabled, colors) {
  if (!productsEnabled || !enabled(form?.sections?.showProducts)) return;

  const cards = form?.catalog?.cards || {};
  const cardImageVisible = Boolean(text(cards.backgroundImageUrl)) && finiteOpacity(cards.backgroundImageOpacity, 0.28) > 0;
  const cardSurface = cardImageVisible
    ? ""
    : resolveAuditSurface(
        text(cards.backgroundColor) || text(colors.surface) || "#FFFFFF",
        finiteOpacity(cards.backgroundOpacity, 1)
      );

  if (cardSurface) {
    [
      ["showType", "typeColor", colors.primary || "#F35A3A", "Tipo de las cards"],
      ["showTitle", "titleColor", colors.text || "#1A1A2E", "Título de las cards"],
      ["showCategory", "categoryColor", colors.mutedText || "#5B5D73", "Categoría de las cards"],
      ["showSubcategory", "subcategoryColor", colors.mutedText || "#5B5D73", "Subcategoría de las cards"],
      ["showDescription", "descriptionColor", colors.mutedText || "#5B5D73", "Descripción de las cards"],
      ["showPrice", "priceColor", colors.secondary || colors.text || "#19242F", "Precio de las cards"],
    ].forEach(([visibilityField, colorField, fallback, label]) => {
      const visible = visibilityField === "showSubcategory"
        ? cards[visibilityField] === true
        : cards[visibilityField] !== false;
      if (!visible) return;
      addContrastIssue(issues, {
        foreground: text(cards[colorField]) || fallback,
        background: cardSurface,
        path: `catalog.cards.${colorField}`,
        section: "featured",
        label,
      });
    });
  }

  if (cards.showButton === false) return;

  const buttonBackground = resolveAuditSurface(
    text(cards.buttonBackgroundColor) || text(colors.primary) || "#F35A3A",
    1,
    cardSurface
  );
  if (buttonBackground) {
    addContrastIssue(issues, {
      foreground: text(cards.buttonTextColor) || text(colors.onPrimary) || "#FFFFFF",
      background: buttonBackground,
      path: "catalog.cards.buttonTextColor",
      section: "featured",
      label: "Texto del botón de las cards",
    });
  }

  const hoverBackground = resolveAuditSurface(
    text(cards.buttonHoverBackgroundColor) || text(cards.buttonBackgroundColor) || text(colors.primary) || "#F35A3A",
    1,
    cardSurface
  );
  if (hoverBackground) {
    addContrastIssue(issues, {
      foreground: text(cards.buttonHoverTextColor) || text(cards.buttonTextColor) || text(colors.onPrimary) || "#FFFFFF",
      background: hoverBackground,
      path: "catalog.cards.buttonHoverTextColor",
      section: "featured",
      label: "Texto hover del botón de las cards",
    });
  }
}

function reviewHeroCtaContrast(issues, form, productsEnabled) {
  if (!visibleSection(form, "hero", productsEnabled)) return;
  const hero = form?.hero || {};

  [
    {
      key: "primary",
      label: "Botón principal de Portada",
      style: hero.primaryCtaStyle,
      visible: Boolean(text(hero.primaryCtaLabel) && text(hero.primaryCtaUrl)) &&
        (!hero.primaryCtaRequiresProducts || productsEnabled),
      defaultOpacity: 1,
      defaultHoverOpacity: 1,
    },
    {
      key: "secondary",
      label: "Botón secundario de Portada",
      style: hero.secondaryCtaStyle,
      visible: Boolean(text(hero.secondaryCtaLabel) && text(hero.secondaryCtaUrl)) &&
        (!hero.secondaryCtaRequiresProducts || productsEnabled),
      defaultOpacity: 0.08,
      defaultHoverOpacity: 0.18,
    },
  ].forEach((cta) => {
    if (!cta.visible || !plainObject(cta.style)) return;
    const style = cta.style;
    const textColor = text(style.textColor);
    const backgroundColor = text(style.backgroundColor);
    const normalSurface = textColor && backgroundColor
      ? resolveAuditSurface(backgroundColor, finiteOpacity(style.backgroundOpacity, cta.defaultOpacity))
      : "";

    if (normalSurface) {
      addContrastIssue(issues, {
        foreground: textColor,
        background: normalSurface,
        path: `hero.${cta.key}CtaStyle.textColor`,
        section: "hero",
        label: `${cta.label} · texto`,
      });
    }

    const hoverBackgroundColor = text(style.hoverBackgroundColor);
    const hoverTextColor = text(style.hoverTextColor) || textColor;
    const hoverSurface = hoverTextColor && hoverBackgroundColor
      ? resolveAuditSurface(hoverBackgroundColor, finiteOpacity(style.hoverBackgroundOpacity, cta.defaultHoverOpacity))
      : "";

    if (hoverSurface) {
      addContrastIssue(issues, {
        foreground: hoverTextColor,
        background: hoverSurface,
        path: `hero.${cta.key}CtaStyle.hoverTextColor`,
        section: "hero",
        label: `${cta.label} · texto hover`,
      });
    }
  });
}

function visibleSection(form, id, productsEnabled) {
  const sections = form?.sections || {};
  if (id === "navbar") return enabled(sections.showNavbar);
  if (id === "hero") return enabled(sections.showHero);
  if (id === "about") return enabled(sections.showAbout);
  if (id === "highlights") return enabled(sections.showHighlights);
  if (id === "contact") return enabled(sections.showContact);
  if (id === "floatingWhatsapp") return enabled(sections.showFloatingWhatsapp);
  if (["spotlight", "featured", "brands", "categories"].includes(id)) {
    if (!productsEnabled || !enabled(sections.showProducts)) return false;
    if (id === "spotlight") return enabled(sections.showSpotlight) && form?.catalog?.spotlight?.enabled === true;
    if (id === "featured") return enabled(sections.showFeatured);
    if (id === "brands") return enabled(sections.showBrands);
    if (id === "categories") return enabled(sections.showCategories);
  }
  return true;
}

function reviewImageAlternativeText(issues, form, productsEnabled) {
  const hero = form?.hero || {};
  const catalog = form?.catalog || {};
  const about = form?.about || {};
  const highlights = Array.isArray(form?.highlights) ? form.highlights : [];

  if (visibleSection(form, "hero", productsEnabled)) {
    const slides = Array.isArray(hero.slides) ? hero.slides.filter((slide) => text(slide?.url)) : [];
    slides.forEach((slide, index) => {
      if (text(slide.alt)) return;
      addIssue(issues, {
        category: "Imágenes",
        severity: "warning",
        path: `hero.slides.${index}.alt`,
        section: "hero",
        tool: "content",
        title: `Imagen ${index + 1} de portada sin texto alternativo`,
        description: "Si la imagen aporta información, describila brevemente. Si es puramente decorativa, podés mantener el texto alternativo vacío.",
      });
    });
    if (!slides.length && text(hero.imageUrl) && !text(hero.title) && !text(form?.business?.name)) {
      addIssue(issues, {
        category: "Imágenes",
        severity: "warning",
        path: "hero.imageUrl",
        section: "hero",
        tool: "content",
        title: "La imagen principal no tiene una descripción derivable",
        description: "La versión pública usa el título de portada como alternativa cuando no hay carrusel. Agregá un título si la imagen comunica contenido.",
      });
    }
  }

  if (visibleSection(form, "spotlight", productsEnabled) && text(catalog.spotlight?.logoUrl) && !text(catalog.spotlight?.logoAlt)) {
    addIssue(issues, {
      category: "Imágenes",
      severity: "warning",
      path: "catalog.spotlight.logoAlt",
      section: "spotlight",
      tool: "content",
      title: "Logo del destacado sin texto alternativo",
      description: "Los logos suelen comunicar identidad. Agregá el nombre de la marca o negocio, salvo que la misma información ya esté repetida justo al lado.",
    });
  }

  if (visibleSection(form, "brands", productsEnabled)) {
    const brands = Array.isArray(catalog.brands?.items) ? catalog.brands.items : [];
    brands.forEach((brand, index) => {
      if (!text(brand?.logoUrl) || text(brand?.alt) || text(brand?.name)) return;
      addIssue(issues, {
        category: "Imágenes",
        severity: "warning",
        path: `catalog.brands.items.${index}.alt`,
        section: "brands",
        tool: "content",
        title: `Marca ${index + 1} sin nombre ni texto alternativo`,
        description: "El logo queda sin una alternativa textual clara. Indicá el nombre de la marca o un texto alternativo breve.",
      });
    });
  }

  if (visibleSection(form, "about", productsEnabled) && text(about.imageUrl) && !text(about.imageAlt)) {
    addIssue(issues, {
      category: "Imágenes",
      severity: "info",
      path: "about.imageAlt",
      section: "about",
      tool: "content",
      title: "Revisá la imagen de “Sobre el negocio”",
      description: "No tiene texto alternativo. Si aporta información distinta del texto cercano, describila; si es decorativa, dejarla vacía es válido.",
    });
  }

  if (visibleSection(form, "highlights", productsEnabled)) {
    highlights.forEach((item, index) => {
      if (!text(item?.imageUrl) || text(item?.imageAlt)) return;
      const imageOnly = !text(item?.title) && !text(item?.description);
      addIssue(issues, {
        category: "Imágenes",
        severity: imageOnly ? "warning" : "info",
        path: `highlights.${index}.imageAlt`,
        section: "highlights",
        tool: "content",
        title: imageOnly
          ? `Diferencial ${index + 1}: imagen sin alternativa textual`
          : `Diferencial ${index + 1}: revisá el texto alternativo`,
        description: imageOnly
          ? "La imagen es el contenido principal de esta tarjeta y necesita una descripción para no perder información."
          : "Si la imagen agrega información que no está en el título o la descripción, sumá un texto alternativo. Si sólo decora, puede quedar vacío.",
      });
    });
  }
}

function reviewControlLabels(issues, form, productsEnabled) {
  const navigation = form?.navigation || {};
  const sections = form?.sections || {};
  const contact = form?.contact || {};
  const business = form?.business || {};

  if (visibleSection(form, "navbar", productsEnabled)) {
    [
      ["navigation.menuAriaLabel", navigation.menuAriaLabel, "Botón para abrir el menú sin descripción accesible"],
      ["navigation.closeMenuAriaLabel", navigation.closeMenuAriaLabel, "Botón para cerrar el menú sin descripción accesible"],
    ].forEach(([path, value, title]) => {
      if (text(value)) return;
      addIssue(issues, {
        category: "Controles",
        severity: "warning",
        path,
        section: "navbar",
        tool: "content",
        title,
        description: "En celular y tablet este control se identifica principalmente por su etiqueta accesible. Agregá una frase corta y concreta.",
      });
    });

    if (productsEnabled && navigation.showCart !== false && !text(navigation.cartAriaLabel)) {
      addIssue(issues, {
        category: "Controles",
        severity: "warning",
        path: "navigation.cartAriaLabel",
        section: "navbar",
        tool: "content",
        title: "Carrito sin descripción accesible clara",
        description: "El icono del carrito necesita una etiqueta que explique su acción, por ejemplo “Abrir carrito”.",
      });
    }

    if (navigation.showCustomerAccess !== false && !text(navigation.accountLabel)) {
      addIssue(issues, {
        category: "Controles",
        severity: "info",
        path: "navigation.accountLabel",
        section: "navbar",
        tool: "content",
        title: "Acceso a cuenta sin nombre accesible",
        description: "Cuando el usuario ya inició sesión, el botón de cuenta puede mostrarse sólo como icono. Sumá una etiqueta corta.",
      });
    }
  }

  if (
    visibleSection(form, "floatingWhatsapp", productsEnabled) &&
    enabled(sections.showFloatingWhatsapp) &&
    text(business.whatsapp) &&
    !text(contact.floatingWhatsappAriaLabel)
  ) {
    addIssue(issues, {
      category: "Controles",
      severity: "warning",
      path: "contact.floatingWhatsappAriaLabel",
      section: "floatingWhatsapp",
      tool: "content",
      title: "WhatsApp flotante sin descripción accesible",
      description: "El botón flotante es sólo un icono. Indicá su acción con una frase breve, por ejemplo “Contactar por WhatsApp”.",
    });
  }
}

function reviewHeadingHierarchy(issues, form, productsEnabled) {
  const hero = form?.hero || {};
  const catalog = form?.catalog || {};
  const about = form?.about || {};
  const highlights = Array.isArray(form?.highlights) ? form.highlights : [];
  const contact = form?.contact || {};
  const business = form?.business || {};

  if (visibleSection(form, "hero", productsEnabled)) {
    const slides = Array.isArray(hero.slides) ? hero.slides.filter((slide) => text(slide?.url)) : [];
    const hasHeroContent = Boolean(
      text(hero.subtitle) || text(hero.eyebrow) || text(hero.imageUrl) || slides.length ||
      text(hero.primaryCtaLabel) || text(hero.secondaryCtaLabel)
    );
    const hasHeading = slides.length
      ? slides.some((slide) => text(slide?.title) || (!Object.prototype.hasOwnProperty.call(slide || {}, "title") && text(hero.title)))
      : text(hero.title) || (!Object.prototype.hasOwnProperty.call(hero, "title") && text(business.name));
    if (hasHeroContent && !hasHeading) {
      addIssue(issues, {
        category: "Jerarquía",
        severity: "warning",
        path: "hero.title",
        section: "hero",
        tool: "content",
        title: "La portada no tiene un título principal claro",
        description: "Hay contenido visible pero no un encabezado principal. Un título breve ayuda a entender rápidamente de qué trata la página.",
      });
    }
  }

  const hierarchySections = [
    {
      id: "about",
      title: about.title,
      hasContent: text(about.description) || text(about.imageUrl) || (text(about.ctaLabel) && text(about.ctaUrl)),
      path: "about.title",
      label: "Sobre el negocio",
    },
    {
      id: "highlights",
      title: form?.highlightsIntro?.title,
      hasContent: highlights.some((item) => text(item?.title) || text(item?.description) || text(item?.imageUrl)),
      path: "highlightsIntro.title",
      label: "Diferenciales",
    },
    {
      id: "contact",
      title: contact.title,
      hasContent: text(contact.subtitle) || text(business.email) || text(business.phone) || text(business.address) || text(business.whatsapp),
      path: "contact.title",
      label: "Contacto",
    },
  ];

  hierarchySections.forEach((entry) => {
    if (!visibleSection(form, entry.id, productsEnabled) || !entry.hasContent || text(entry.title)) return;
    addIssue(issues, {
      category: "Jerarquía",
      severity: "info",
      path: entry.path,
      section: entry.id,
      tool: "content",
      title: `${entry.label} tiene contenido pero no un título de sección`,
      description: "No bloquea la página, pero un encabezado ayuda a lectores y tecnologías de asistencia a entender la estructura.",
    });
  });

  [
    ["featured", catalog.featured, "Productos destacados"],
    ["brands", catalog.brands, "Marcas"],
    ["categories", catalog.categories, "Categorías"],
  ].forEach(([id, block, label]) => {
    if (!visibleSection(form, id, productsEnabled)) return;
    const hasContent = text(block?.subtitle) || text(block?.eyebrow) ||
      (id === "brands" && Array.isArray(block?.items) && block.items.length > 0);
    if (!hasContent || text(block?.title)) return;
    addIssue(issues, {
      category: "Jerarquía",
      severity: "info",
      path: `catalog.${id}.title`,
      section: id,
      tool: "content",
      title: `${label} no tiene un encabezado de sección`,
      description: "Si el bloque es importante para recorrer la página, un título visible mejora la orientación. Si el contexto ya es suficiente, podés dejarlo así.",
    });
  });
}

function reviewContrast(issues, form, productsEnabled) {
  const theme = form?.theme || {};
  const colors = {
    primary: theme.primaryColor || "#F35A3A",
    secondary: theme.secondaryColor || "#19242F",
    surface: theme.surfaceColor || "#FFFFFF",
    background: theme.backgroundColor || "#EEF0F5",
    text: theme.textColor || "#1A1A2E",
    mutedText: theme.mutedTextColor || "#5B5D73",
    divider: theme.dividerColor || "#E1E4EA",
    onPrimary: "#FFFFFF",
    onSecondary: "#FFFFFF",
  };
  const appearance = form?.appearance || {};

  if (visibleSection(form, "navbar", productsEnabled)) {
    addContrastIssue(issues, {
      foreground: colors.secondary,
      background: colors.surface,
      path: "theme.secondaryColor",
      section: "navbar",
      label: "Texto destacado del encabezado",
    });
    addContrastIssue(issues, {
      foreground: colors.text,
      background: colors.surface,
      path: "theme.textColor",
      section: "navbar",
      label: "Texto del encabezado",
    });
  }

  const aboutBackground = appearance.about?.surfaceColor || appearance.about?.backgroundColor || theme.backgroundColor || colors.background;
  if (visibleSection(form, "spotlight", productsEnabled)) {
    const spotlight = form?.catalog?.spotlight || {};
    const spotlightPrimary = spotlight.primaryColor || theme.primaryColor || colors.primary;
    const spotlightSecondary = spotlight.secondaryColor || theme.secondaryColor || colors.secondary;
    const whiteOnPrimary = landingContrastRatio("#FFFFFF", spotlightPrimary);
    const whiteOnSecondary = landingContrastRatio("#FFFFFF", spotlightSecondary);
    const weakestTextRatio = [whiteOnPrimary, whiteOnSecondary]
      .filter((ratio) => ratio !== null)
      .reduce((minimum, ratio) => Math.min(minimum, ratio), Infinity);
    if (Number.isFinite(weakestTextRatio) && weakestTextRatio < 3) {
      addIssue(issues, {
        category: "Contraste",
        severity: "warning",
        path: "catalog.spotlight.primaryColor",
        section: "spotlight",
        tool: "colors",
        title: "El texto blanco del destacado puede perderse sobre el fondo",
        description: `La zona más clara del degradado queda cerca de ${weakestTextRatio.toFixed(1)}:1. Probá oscurecer uno de los colores del destacado o elegí una combinación más legible.`,
        detail: "Referencia orientativa: 3:1 para texto grande y 4.5:1 para texto normal.",
      });
    }
    if (text(spotlight.ctaLabel) && text(spotlight.ctaUrl)) {
      addContrastIssue(issues, {
        foreground: spotlightPrimary,
        background: "#FFFFFF",
        path: "catalog.spotlight.primaryColor",
        section: "spotlight",
        label: "Texto del botón del destacado",
      });
    }
  }

  if (visibleSection(form, "about", productsEnabled)) {
    addContrastIssue(issues, {
      foreground: appearance.about?.titleColor || theme.secondaryColor || colors.secondary,
      background: aboutBackground,
      path: "appearance.about.titleColor",
      section: "about",
      label: "Título de “Sobre el negocio”",
    });
    addContrastIssue(issues, {
      foreground: appearance.about?.textColor || theme.mutedTextColor || colors.mutedText,
      background: aboutBackground,
      path: "appearance.about.textColor",
      section: "about",
      label: "Texto de “Sobre el negocio”",
    });
  }

  if (visibleSection(form, "highlights", productsEnabled)) {
    const highlightsBackground = appearance.highlights?.backgroundColor || theme.surfaceColor || colors.surface;
    const cardBackground = appearance.highlights?.surfaceColor || theme.surfaceColor || colors.surface;
    addContrastIssue(issues, {
      foreground: appearance.highlights?.titleColor || theme.secondaryColor || colors.secondary,
      background: highlightsBackground,
      path: "appearance.highlights.titleColor",
      section: "highlights",
      label: "Título de Diferenciales",
    });
    addContrastIssue(issues, {
      foreground: appearance.highlights?.textColor || theme.mutedTextColor || colors.mutedText,
      background: cardBackground,
      path: "appearance.highlights.textColor",
      section: "highlights",
      label: "Texto de las tarjetas de Diferenciales",
    });
  }

  if (visibleSection(form, "contact", productsEnabled)) {
    const contactBackground = appearance.contact?.backgroundColor || theme.secondaryColor || colors.secondary;
    addContrastIssue(issues, {
      foreground: appearance.contact?.titleColor || "#FFFFFF",
      background: contactBackground,
      path: "appearance.contact.titleColor",
      section: "contact",
      label: "Título de Contacto",
    });
    addContrastIssue(issues, {
      foreground: appearance.contact?.textColor || "rgba(255,255,255,.82)",
      background: contactBackground,
      path: "appearance.contact.textColor",
      section: "contact",
      label: "Texto de Contacto",
    });
  }

  reviewCatalogCardContrast(issues, form, productsEnabled, colors);
  reviewHeroCtaContrast(issues, form, productsEnabled);
}

export function buildLandingAccessibilityAudit(form = {}, productsEnabled = true) {
  const issues = [];
  reviewImageAlternativeText(issues, form, productsEnabled);
  reviewControlLabels(issues, form, productsEnabled);
  reviewHeadingHierarchy(issues, form, productsEnabled);
  reviewContrast(issues, form, productsEnabled);

  const severityOrder = { warning: 0, info: 1 };
  issues.sort((left, right) => {
    const severity = (severityOrder[left.severity] ?? 9) - (severityOrder[right.severity] ?? 9);
    if (severity !== 0) return severity;
    return `${left.sectionLabel}:${left.title}`.localeCompare(`${right.sectionLabel}:${right.title}`, "es");
  });

  const warnings = issues.filter((issue) => issue.severity === "warning").length;
  return {
    issues,
    warnings,
    suggestions: issues.length - warnings,
    checkedAt: new Date().toISOString(),
  };
}
